import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/pages/portal/components/PortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { getMyOrders } from "@/services/adminService";
import type { ErpOrder, ErpOrderStatus } from "@/types/admin";
import { downloadCsv } from "@/utils/csv";

const STATUS_BADGE: Record<ErpOrderStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-secondary-100 text-secondary-800",
  processing: "bg-primary-100 text-primary-700",
  fulfilled: "bg-emerald-100 text-emerald-800",
  refunded: "bg-rose-100 text-rose-700",
  cancelled: "bg-background-200 text-foreground-600",
};

const STATUS_LABEL: Record<ErpOrderStatus, string> = {
  pending: "Awaiting payment",
  paid: "Paid",
  processing: "Processing",
  fulfilled: "Completed",
  refunded: "Refunded",
  cancelled: "Cancelled",
};

const inr = (n: number) => `₹${n.toLocaleString("en-IN")}`;
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function PortalOrdersPage() {
  const { accessToken } = useAuth();
  const [orders, setOrders] = useState<ErpOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"all" | ErpOrderStatus>("all");

  const load = useCallback(async () => {
    if (!accessToken) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const rows = await getMyOrders(accessToken);
      setOrders(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We could not load your orders. Please try again.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const spent = orders
      .filter((o) => o.status === "paid" || o.status === "fulfilled" || o.status === "processing")
      .reduce((s, o) => s + o.amount, 0);
    const active = orders.filter((o) => o.status === "paid" || o.status === "processing" || o.status === "fulfilled").length;
    return { count: orders.length, spent, active, last: orders[0]?.createdAt ?? null };
  }, [orders]);

  const filtered = useMemo(
    () => (status === "all" ? orders : orders.filter((o) => o.status === status)),
    [orders, status],
  );

  function exportCsv() {
    downloadCsv(
      `my-orders-${new Date().toISOString().slice(0, 10)}`,
      ["Order No", "Plan", "Business", "Amount", "Currency", "Payment", "Status", "Date"],
      filtered.map((o) => [o.orderNo, o.plan, o.business, o.amount, o.currency, o.paymentMethod, o.status, fmtDate(o.createdAt)]),
    );
  }

  const cards = [
    { label: "Total Orders", value: String(stats.count), icon: "ri-shopping-bag-3-line", tone: "text-primary-600 bg-primary-50" },
    { label: "Amount Paid", value: inr(stats.spent), icon: "ri-money-rupee-circle-line", tone: "text-emerald-600 bg-emerald-50" },
    { label: "Active Plans", value: String(stats.active), icon: "ri-checkbox-circle-line", tone: "text-accent-600 bg-accent-50" },
    { label: "Last Order", value: stats.last ? fmtDate(stats.last) : "—", icon: "ri-calendar-line", tone: "text-secondary-600 bg-secondary-50" },
  ];

  return (
    <PortalLayout>
      <div className="max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">My Orders</h1>
            <p className="mt-1 text-sm text-foreground-600">
              Every listing plan you've purchased and its payment status.
            </p>
          </div>
          <Link
            to="/listing-plans"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-sm" /> Buy or renew a plan
          </Link>
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${c.tone}`}>
                <i className={`${c.icon} text-lg`} />
              </div>
              <p className="font-heading text-xl font-bold text-foreground-950">{loading ? "—" : c.value}</p>
              <p className="mt-0.5 text-xs text-foreground-500">{c.label}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
              {error}
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        )}

        {/* Orders table */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="flex flex-col gap-3 border-b border-background-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              Order history
              <span className="ml-2 text-xs font-normal text-foreground-500">
                {filtered.length} order{filtered.length !== 1 ? "s" : ""}
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as "all" | ErpOrderStatus)}
                className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 outline-none focus:border-primary-400"
                aria-label="Filter orders by status"
              >
                <option value="all">All statuses</option>
                {(Object.keys(STATUS_LABEL) as ErpOrderStatus[]).map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={exportCsv}
                disabled={loading || filtered.length === 0}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="ri-download-2-line text-sm" /> Export
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-receipt-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm font-medium text-foreground-700">No orders yet</p>
              <p className="mt-1 text-xs text-foreground-500">Your listing plan purchases will appear here.</p>
              <Link
                to="/listing-plans"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-store-2-line text-sm" /> Explore listing plans
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead className="border-b border-background-200 bg-background-100/60">
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-foreground-500">
                    <th className="px-6 py-3">Order</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((o) => (
                    <tr key={o.id} className="hover:bg-background-100/50">
                      <td className="px-6 py-4 text-sm font-semibold text-foreground-900">{o.orderNo}</td>
                      <td className="px-4 py-4 text-sm text-foreground-700">{o.plan}</td>
                      <td className="px-4 py-4 text-sm text-foreground-700">{o.business}</td>
                      <td className="px-4 py-4 text-sm font-semibold tabular-nums text-foreground-900">
                        {o.amount > 0 ? inr(o.amount) : "Free"}
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground-600">{o.paymentMethod}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_BADGE[o.status]}`}>
                          {STATUS_LABEL[o.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-xs text-foreground-500">{fmtDate(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-information-line text-base" />
          Need help with a payment? Contact support at support@coastalbizz.in.
        </p>
      </div>
    </PortalLayout>
  );
}